import {
	Stack,
	FormControl,
	TextField,
	Select,
	OutlinedInput,
	InputLabel,
	MenuItem,
	Box,
	Typography,
	Divider,
	useFormControl,
	useTheme,
	CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import FormErrorMessage from '../../../components/Form/FormErrorMessage';
import AccountType from '../../../components/Form/AccountType';
import RoundedButton from '../../../components/RoundedButton/RoundedButton';
import { ColorBaseCharcoal, ColorBaseGreenEnergy } from '@huntington/honeycomb-design';
import React, { useEffect, useMemo, useRef } from 'react';
import { useGetRolesQuery } from '../../../api/admin.api.ts';
import { ADD_ACCOUNT, BACK, CONTINUE, REMOVE_ACCOUNT } from '../../../constants/messages.tsx';
import DirtyFormModal from '../../../components/Modals/DirtyFormModal/DirtyFormModal.tsx';
import useFormHook from '../../../hooks/useFormHook.ts';
import type { ConfirmUserFormAccounts } from '../ConfirmUser/ConfirmUser.tsx';
import { phoneTypeOptions } from '../referenceOptions.ts';
import type { UserFormMutation } from '../../../api/models/admin.model.ts';
import type { RoleName } from '../../../api/models/user.model.ts';

type UserFormBase = Omit<UserFormMutation, 'externalUserAccounts'>;

export interface Accounts {
	roleId: { label: string; value: string };
	accountId: { label: string; value: string };
}

export interface UserForm extends UserFormBase {
	externalUserAccounts: Accounts[];
}

type UserManagementFormProps = {
	userForm: UserForm;
	triggerNavigation: (payload: UserForm) => void;
};

function UserManagementForm(props: UserManagementFormProps) {
	const { userForm, triggerNavigation } = props;

	const bypassDirtyModalRef = useRef(false);
	const navigate = useNavigate();
	const location = useLocation();
	const { formState } = location.state ?? {};
	const theme = useTheme();
	const { isDirty, setIsDirty, showDirtyModal, setShowDirtyModal, blockerProceed, validate } =
		useFormHook();
	const formControl = useFormControl();

	const [firstName, setFirstName] = React.useState<string>('');
	const [lastName, setLastName] = React.useState<string>('');
	const [phoneType, setPhoneType] = React.useState<string>('');
	const [phoneNumber, setPhoneNumber] = React.useState<string>('');
	const [email, setEmail] = React.useState<string>('');
	const [accounts, setAccounts] = React.useState<Accounts[]>([]);

	const [firstNameError, setFirstNameError] = React.useState<string>('');
	const [lastNameError, setLastNameError] = React.useState<string>('');
	const [phoneNumberError, setPhoneNumberError] = React.useState<string>('');
	const [emailError, setEmailError] = React.useState<string>('');
	const [roleError, setRoleError] = React.useState<string[]>([]);
	const [accountError, setAccountError] = React.useState<string[]>([]);

	const handleDirtiness = () => {
		if (!isDirty) {
			setIsDirty(true);
		}
	};

	useEffect(() => {
		if (formState) {
			// Populate from navigation state (edit/confirm)
			setFirstName(formState.firstName);
			setLastName(formState.lastName);
			setPhoneType(formState.phoneType ?? '');
			setPhoneNumber(formState.phoneNumber ?? '');
			setEmail(formState.emailAddress);
			setAccounts(
				formState.externalUserAccounts.map((account: ConfirmUserFormAccounts) => ({
					accountId: { label: account.accountLabel || '', value: String(account.accountId) || '' },
					roleId: { label: account.roleLabel, value: String(account.roleId) },
				}))
			);
		} else if (userForm) {
			// Populate from initial empty form (add)
			setFirstName(userForm.firstName);
			setLastName(userForm.lastName);
			setPhoneType(userForm.phoneType ?? '');
			setPhoneNumber(userForm.phoneNumber ?? '');
			setEmail(userForm.emailAddress);
			setAccounts(
				userForm.emailAddress
					? userForm.externalUserAccounts
					: [
							{
								accountId: { label: '', value: '' },
								roleId: { label: '', value: '' },
							},
						]
			);
		}
	}, [userForm, formState]);

	const handleContinue = (_: never) => {
		const newFirstNameError = validate.validateFirstName(firstName);
		const newLastNameError = validate.validateLastName(lastName);
		const newEmailError = validate.validateEmail(email);
		const newPhoneNumberError = validate.validatePhoneNumber(phoneNumber);

		const newRoleErrors: string[] = [];
		const newAccountErrors: string[] = [];

		accounts.forEach((account, index) => {
			newRoleErrors[index] = validate.validateRoleId(account.roleId.value);
			newAccountErrors[index] = validate.validateAccountId(
				account.accountId,
				accounts.filter((_, i) => index !== i).map((acc) => acc.accountId.value)
			);
		});

		if (
			newFirstNameError ||
			newLastNameError ||
			newEmailError ||
			newPhoneNumberError ||
			newRoleErrors.some((err) => err) ||
			newAccountErrors.some((err) => err)
		) {
			setFirstNameError(newFirstNameError);
			setLastNameError(newLastNameError);
			setEmailError(newEmailError);
			setPhoneNumberError(newPhoneNumberError);
			setRoleError(newRoleErrors);
			setAccountError(newAccountErrors);

			return;
		}

		triggerNavigation({
			userType: 'external',
			firstName,
			lastName,
			phoneType: phoneType || null,
			phoneNumber: phoneNumber || null,
			emailAddress: email,
			externalUserAccounts: accounts,
		});

		// Set the ref and trigger navigation in useEffect
		bypassDirtyModalRef.current = true;
		setIsDirty(false);
	};

	const { data: roles, isLoading, isFetching } = useGetRolesQuery();
	const externalRoleOptions = useMemo(() => {
		// remove 'Ext ' from the role name
		const formattedRoles = roles?.externalRoles
			? structuredClone(roles.externalRoles).sort((a, b) => a.roleName.localeCompare(b.roleName))
			: [];
		formattedRoles.forEach((role) => {
			role.roleName = role.roleName.replace('Ext ', '') as RoleName;
		});
		return formattedRoles;
	}, [roles]);

	if (isLoading || isFetching) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Stack spacing={3}>
			<Typography variant="h5" sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}>
				User Details
			</Typography>
			<FormControl required>
				<TextField
					label="First Name"
					value={firstName}
					slotProps={{ inputLabel: { shrink: true } }}
					onChange={(e) => {
						setFirstName(e.target.value);
						handleDirtiness();
					}}
					onBlur={() => {
						setFirstNameError(validate.validateFirstName(firstName));
					}}
					error={!!firstNameError}
					required
				/>
				{firstNameError && <FormErrorMessage message={firstNameError} />}
			</FormControl>
			<FormControl required>
				<TextField
					label="Last Name"
					value={lastName}
					slotProps={{ inputLabel: { shrink: true } }}
					onChange={(e) => {
						setLastName(e.target.value);
						handleDirtiness();
					}}
					onBlur={() => {
						setLastNameError(validate.validateLastName(lastName));
					}}
					error={!!lastNameError}
					required
				/>
				{lastNameError && <FormErrorMessage message={lastNameError} />}
			</FormControl>
			<Divider />
			<Typography variant="h5" sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}>
				Contact Information
			</Typography>
			<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
				<FormControl>
					<InputLabel id="selected-phone-type-label" shrink>
						Phone Type (Optional)
					</InputLabel>
					<Select
						labelId="selected-phone-type-label"
						id={`selected-phoneType`}
						value={phoneType}
						label={'Phone Type (Optional)'}
						onChange={(e) => {
							setPhoneType(e.target.value);
							handleDirtiness();
						}}
						input={<OutlinedInput label="Phone Type (Optional)" notched={true} />}
					>
						{phoneTypeOptions.map((option) => (
							<MenuItem
								key={String(option.value)}
								value={String(option.value)}
								datatest-id={`phoneType-${option.value}`}
							>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl>
					<TextField
						label="Phone Number (Optional)"
						value={phoneNumber}
						slotProps={{ inputLabel: { shrink: true } }}
						onChange={(e) => {
							setPhoneNumber(e.target.value);
							handleDirtiness();
						}}
						error={!!phoneNumberError}
					/>
					{phoneNumberError && <FormErrorMessage message={phoneNumberError} />}
				</FormControl>
			</Stack>
			<FormControl required>
				<TextField
					label="Email"
					value={email}
					slotProps={{ inputLabel: { shrink: true } }}
					onChange={(e) => {
						setEmail(e.target.value);
						handleDirtiness();
					}}
					onBlur={() => {
						setEmailError(validate.validateEmail(email));
					}}
					error={!!emailError}
					required
				/>
				{emailError && <FormErrorMessage message={emailError} />}
			</FormControl>
			<Divider />
			<Typography variant="h5" sx={{ color: ColorBaseCharcoal, fontWeight: 'bold' }}>
				Account Information
			</Typography>
			<Stack spacing={{ xs: 6, sm: 6, md: 6, lg: 2 }}>
				{accounts.map((account, index) => (
					<Stack
						key={index}
						direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
						spacing={2}
					>
						<FormControl required error={!!roleError[index]}>
							<InputLabel id="selected-role-type" shrink={true}>
								Role:
							</InputLabel>
							<Select
								label={'Role'}
								value={account.roleId.value}
								onChange={(e) => {
									const newAccounts = [...accounts];
									const matchedRole = externalRoleOptions.find(
										(role) => String(role.id) === String(e.target.value)
									);
									newAccounts[index].roleId = {
										label: matchedRole ? matchedRole.roleName : '',
										value: String(e.target.value),
									};
									setAccounts(newAccounts);

									const newRoleErrors = [...roleError];
									newRoleErrors[index] = validate.validateRoleId(account.roleId.value);
									setRoleError(newRoleErrors);

									handleDirtiness();
								}}
								onClose={() => {
									const newRoleErrors = [...roleError];
									newRoleErrors[index] = validate.validateRoleId(account.roleId.value);
									setRoleError(newRoleErrors);
								}}
								input={<OutlinedInput label="Role:" notched={true} />}
								error={!!roleError[index]}
								required={formControl?.required}
								sx={{ minWidth: '460px' }}
							>
								{externalRoleOptions.map((option) => (
									<MenuItem
										key={String(option.id)}
										value={String(option.id)}
										datatest-id={`roleId-${index}-${option.id}`}
									>
										{option.roleName}
									</MenuItem>
								))}
							</Select>
							{roleError[index] && <FormErrorMessage message={roleError[index]} />}
						</FormControl>
						<FormControl required error={!!accountError[index]}>
							<AccountType
								id={index}
								name="add-user-account"
								value={account.accountId}
								onChange={(_, newValue) => {
									const newAccounts = [...accounts];
									newAccounts[index].accountId = newValue;
									setAccounts(newAccounts);

									const newAccountErrors = [...accountError];
									newAccountErrors[index] = validate.validateAccountId(
										account.accountId,
										accounts.filter((_, i) => index !== i).map((acc) => acc.accountId.value)
									);
									setAccountError(newAccountErrors);

									handleDirtiness();
								}}
								onClose={() => {
									const newAccountErrors = [...accountError];
									newAccountErrors[index] = validate.validateAccountId(
										account.accountId,
										accounts.filter((_, i) => index !== i).map((acc) => acc.accountId.value)
									);
									setAccountError(newAccountErrors);
								}}
								error={!!accountError[index]}
							/>
							{accountError[index] && <FormErrorMessage message={accountError[index]} />}
						</FormControl>

						{index > 0 && (
							<Box
								sx={{
									color: ColorBaseGreenEnergy,
									fontWeight: 'bold',
									fontSize: 13,
									cursor: 'pointer',
									display: 'flex',
									pt: { xs: 0.5, sm: 0.5, md: 0.5, lg: 2.5 },
								}}
								onClick={() => {
									setAccounts(accounts.filter((_, i) => i !== index));
									setAccountError(accountError.filter((_, i) => i !== index));
									setRoleError(roleError.filter((_, i) => i !== index));
								}}
							>
								{REMOVE_ACCOUNT}
							</Box>
						)}
					</Stack>
				))}
			</Stack>
			<span
				style={{
					color: theme.palette.brand.primary,
					fontWeight: 'bold',
					fontSize: 13,
					cursor: 'pointer',
				}}
				onClick={() => {
					setAccounts([
						...accounts,
						{ accountId: { label: '', value: '' }, roleId: { label: '', value: '' } },
					]);
				}}
			>
				{ADD_ACCOUNT}
			</span>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
				<RoundedButton
					variantStyle="light"
					variant="outlined"
					onClick={() => navigate('/user-management')}
				>
					{BACK}
				</RoundedButton>
				{}
				<RoundedButton variantStyle="dark" variant="contained" onClick={handleContinue}>
					{CONTINUE}
				</RoundedButton>
			</Box>
			<DirtyFormModal
				open={showDirtyModal}
				onClose={() => setShowDirtyModal(false)}
				onNavigate={blockerProceed}
			/>
		</Stack>
	);
}

export default UserManagementForm;
